import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import { getDeviceMap } from '../../Api/MapApi';
import MapboxLanguage from '@mapbox/mapbox-gl-language';

// import mapboxgl from 'mapbox-gl';
import MapGL, { Source, Layer, Popup } from 'react-map-gl/mapbox';


const MAPBOX_TOKEN = 'pk.eyJ1Ijoib21vZWdvZCIsImEiOiJjbG5pbzd6bWQwOWM0MmlzM2l6cGdoencwIn0.i3XwPBMrrr1g2n3_xr7YLQ';


export const MapBox = () => {
    const navigate = useNavigate();
    const [lng, setLng] = useState(28.030535);
    const [lat, setLat] = useState(53.850905);
    const [zoom, setZoom] = useState(7);
    const [geojsonData, setGeojsonData] = useState(null);
    const [popupInfo, setPopupInfo] = useState(null);
    const mapRef = useRef(null);
    const [viewState, setViewState] = useState({
        longitude: 28.030535,
        latitude: 53.850905,
        zoom: 7
    });

    const generatePopupContent = (devices) => {
        const totalDevices = devices.length;
        const onlineDevices = devices.filter((device) => device.data.status === 'ONLINE').length;
        const offlineDevices = devices.filter((device) => device.data.status === 'WARN').length;
        const warnDevices = devices.filter((device) => device.data.status === 'PROBLEM').length;

        return `
            <div>
                <p>Всего устройств: <span style="color: blue">${totalDevices} </span></p>
                <p>В сети: <span style="color: green">${onlineDevices}</span></p>
                <p>С ошибками: <span style="color: orange">${warnDevices}</span></p>
                <p>Не в сети: <span style="color: red">${offlineDevices}</span></p>
            </div>
        `;
    };

    const calculateBoundsAndCenter = (features) => {
        if (!features || features.length === 0) return null;

        let minLng = Infinity;
        let maxLng = -Infinity;
        let minLat = Infinity;
        let maxLat = -Infinity;

        features.forEach(feature => {
            const [lng, lat] = feature.geometry.coordinates;
            minLng = Math.min(minLng, lng);
            maxLng = Math.max(maxLng, lng);
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
        });

        const centerLng = (minLng + maxLng) / 2;
        const centerLat = (minLat + maxLat) / 2;

        const lngDiff = maxLng - minLng;
        const latDiff = maxLat - minLat;
        const maxDiff = Math.max(lngDiff, latDiff);

        let zoom = 7;
        if (maxDiff < 0.01) zoom = 14;
        else if (maxDiff < 0.05) zoom = 13;
        else if (maxDiff < 0.1) zoom = 12;
        else if (maxDiff < 0.5) zoom = 11;
        else if (maxDiff < 1) zoom = 8;
        else if (maxDiff < 5) zoom = 7;
        else zoom = 6;

        return {
            longitude: centerLng,
            latitude: centerLat,
            zoom: zoom
        };
    };

    const isValidCoordinate = (lng, lat) => {
        return (
            lng !== null && 
            lat !== null && 
            lng !== undefined && 
            lat !== undefined && 
            !isNaN(lng) && 
            !isNaN(lat) && 
            lng !== 0 && 
            lat !== 0 &&
            Math.abs(lng) <= 180 &&
            Math.abs(lat) <= 90
        );
    };

    const mapRefCallback = useCallback((ref) => {
        if (ref !== null) {
            mapRef.current = ref;
            const map = ref.getMap(); 

            const language = new MapboxLanguage({
                defaultLanguage: 'ru'
            });

            map.addControl(language);
        }
    }, []);

    useEffect(() => {
        async function fetchDevices() {
            try {
                const data = await getDeviceMap();

                const coordMap = new Map();
                let filteredCount = 0;

                for (const device of data) {
                    if (!device.address || !device.address[0]) {
                        filteredCount++;
                        continue;
                    }
                    
                    const lng = Number(device.address[0].lng);
                    const lat = Number(device.address[0].lat);
                    
                    if (!isValidCoordinate(lng, lat)) {
                        filteredCount++;
                        continue;
                    }
                    
                    const key = `${lng},${lat}`;

                    if (!coordMap.has(key)) {
                        coordMap.set(key, []);
                    }
                    coordMap.get(key).push(device);
                }

                if (filteredCount > 0) {
                    console.log(`Filtered out ${filteredCount} devices with invalid coordinates`);
                }

                const features = Array.from(coordMap.entries()).map(([key, devicesAtCoord]) => {
                    const [lng, lat] = key.split(',').map(Number);

                    return {
                        type: "Feature",
                        properties: {
                            device_count: devicesAtCoord.length,
                            popupContent: generatePopupContent(devicesAtCoord),
                            address: devicesAtCoord[0].address
                        },
                        geometry: {
                            type: "Point",
                            coordinates: [lng, lat]
                        }
                    };
                });

                const geojson = {
                    type: "FeatureCollection",
                    features: features
                };

                setGeojsonData(geojson);

                const bounds = calculateBoundsAndCenter(features);
                if (bounds) {
                    setViewState(bounds);
                    setLng(bounds.longitude);
                    setLat(bounds.latitude);
                    setZoom(bounds.zoom);
                }

            } catch (error) {
                console.error('Ошибка при загрузке карты:', error.message);
            }

        }

        fetchDevices();
    }, []);


    const onClick = useCallback((event) => {
        const map = mapRef.current;
        if (!map) return;



        const features = map.queryRenderedFeatures(event.point, {
            layers: ['clusters', 'unclustered-point']
        });

        if (!features.length) return;

        const feature = features[0];

        if (feature.layer.id === 'clusters') {
            const clusterId = feature.properties.cluster_id;

            map.getSource('devices').getClusterExpansionZoom(
                clusterId,
                (err, zoom) => {
                    if (err) return;

                    map.easeTo({
                        center: feature.geometry.coordinates,
                        zoom: zoom
                    });
                }
            );
        } else if (feature.layer.id === 'unclustered-point') {
            setPopupInfo({
                coordinates: feature.geometry.coordinates,
                content: feature.properties.popupContent,
                address: feature.properties.address
            });
        }
    }, []);


    const handleNavigate = () => {
        if (popupInfo?.address) {
            navigate("/devices", { state: { address: popupInfo.address } });
        }
    };

    return (
        <div className="map-container" style={{ height: '100vh' }}>
            <MapGL
                ref={mapRefCallback}
                mapboxAccessToken={MAPBOX_TOKEN}
                {...viewState}
                onMove={evt => setViewState(evt.viewState)}
                style={{ width: '100%', height: '100%' }}
                mapStyle="mapbox://styles/mapbox/streets-v12"
                interactiveLayerIds={['unclustered-point']}
                onClick={onClick}
            >
                {geojsonData && (
                    <Source
                        id="devices"
                        type="geojson"
                        data={geojsonData}
                        cluster={true}
                        clusterMaxZoom={14}
                        clusterRadius={50}
                    >
                        <Layer
                            id="clusters"
                            type="circle"
                            source="devices"
                            filter={["has", "point_count"]}
                            paint={{
                                "circle-color": "#466AE9",
                                "circle-radius": [
                                    "step",
                                    ["get", "point_count"],
                                    20,
                                    10,
                                    30,
                                    50,
                                    40
                                ],
                                "circle-stroke-width": 2,
                                "circle-stroke-color": "#3256D5"
                            }}
                        />
                        <Layer
                            id="cluster-count"
                            type="symbol"
                            source="devices"
                            filter={["has", "point_count"]}
                            layout={{
                                "text-field": "{point_count_abbreviated}",
                                "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
                                "text-size": 20,
                            }}
                            paint={{
                                "text-color": "#ffffff"
                            }}
                        />
                        <Layer
                            id="unclustered-point"
                            type="circle"
                            source="devices"
                            filter={["!", ["has", "point_count"]]}
                            paint={{
                                "circle-color": "#466AE9",
                                "circle-radius": 19,
                                "circle-stroke-width": 1,
                                "circle-stroke-color": "#fff"
                            }}
                        />
                        <Layer
                            id="unclustered-count"
                            type="symbol"
                            source="devices"
                            filter={["!", ["has", "point_count"]]}
                            layout={{
                                "text-field": ["to-string", ["get", "device_count"]],
                                "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
                                "text-size": 18
                            }}
                            paint={{
                                "text-color": "#ffffff"
                            }}
                        />
                    </Source>
                )}

                {popupInfo && (
                    <Popup
                        longitude={popupInfo.coordinates[0]}
                        latitude={popupInfo.coordinates[1]}
                        closeOnClick={false}
                        onClose={() => setPopupInfo(null)}
                        anchor="top"
                    >
                        <div dangerouslySetInnerHTML={{ __html: popupInfo.content }} />
                        <button onClick={handleNavigate}>Перейти к устройствам</button>
                    </Popup>
                )}
            </MapGL>
        </div>
    );
};

export default MapBox;